import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { RefreshCw, RotateCcw } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { esQuery } from '@/lib/es'
import { usePersistentState } from '@/hooks/usePersistentState'
import { JsonView, allExpanded, darkStyles } from 'react-json-view-lite'
import 'react-json-view-lite/dist/index.css'

interface ApiLog {
  name: string
  timestamp: number
  request: {
    url: string
    method?: string
    headers?: any
    body?: string
  }
  response?: {
    status?: number
    header?: any
    body?: string
    time?: number
  }
}

const escapeWildcard = (value: string) => value.replace(/([\\*?])/g, '\\$1')

export default function ApiLogs() {
  const [data, setData] = useState<ApiLog[]>([])
  const [account, setAccount, clearAccount] = usePersistentState('admin:api-logs:account', '')
  const [token, setToken, clearToken] = usePersistentState<string | undefined>(
    'admin:api-logs:token',
    undefined,
  )
  const [tokens, setTokens] = useState<any[]>([])
  const [path, setPath, clearPath] = usePersistentState('admin:api-logs:path', '')
  const [loading, setLoading] = useState(false)

  const getData = async () => {
    setLoading(true)
    try {
      const body: any = {
        sort: [{ timestamp: { order: 'desc' } }],
        query: {
          bool: {
            filter: [],
          },
        },
      }

      if (account) {
        body.query.bool.filter.push({
          wildcard: {
            'name.keyword': `*${escapeWildcard(account.trim())}*`,
          },
        })
      }

      if (token) {
        body.query.bool.filter.push({
          term: {
            'request.headers.token.keyword': token,
          },
        })
      }

      if (path) {
        body.query.bool.filter.push({
          wildcard: {
            'request.url.keyword': `*${escapeWildcard(path.trim())}*`,
          },
        })
      }

      const result = await esQuery('apis', body)

      if (result) {
        const rs = result.data ? result.data : result
        const list = rs.hits.hits
          .map((val: any) => val._source)
          .sort((a: ApiLog, b: ApiLog) => b.timestamp - a.timestamp)
        setData(list.slice(0, 400)) // 限制400条
      }
    } catch (error) {
      console.error('Failed to fetch api logs:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadTokens = async () => {
    try {
      const body = {
        aggs: {
          name: {
            terms: {
              size: 100,
              field: 'request.headers.token.keyword',
            },
          },
        },
      }

      const result = await esQuery('apis', body)

      if (result) {
        const rs = result.data ? result.data : result
        setTokens(rs.aggregations.name.buckets)
      }
    } catch (error) {
      console.error('Failed to load tokens:', error)
    }
  }

  useEffect(() => {
    loadTokens()
  }, [])

  useEffect(() => {
    getData()
  }, [account, token, path])

  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleString('zh-CN')
  }

  const handleClearFilters = () => {
    clearAccount()
    clearToken()
    clearPath()
  }

  const parseJson = (str: string) => {
    if (!str) return null
    try {
      if (str.indexOf('{') > -1 || str.indexOf('[') > -1) {
        return JSON.parse(str)
      }
    } catch (error) {
      // 解析失败返回原字符串
    }
    return str
  }

  const JsonPopover = ({ data, title }: { data: any; title: string }) => {
    const parsedData = typeof data === 'string' ? parseJson(data) : data

    if (!parsedData) {
      return <span className="text-muted-foreground">null</span>
    }

    // 暗黑主题 JSON 样式
    const customDarkJsonStyle = {
      ...darkStyles,
      container: 'font-mono text-sm',
      label: 'text-sky-400 font-semibold',
      nullValue: 'text-gray-500',
      undefinedValue: 'text-gray-500',
      stringValue: 'text-emerald-400',
      booleanValue: 'text-purple-400',
      numberValue: 'text-amber-400',
      otherValue: 'text-gray-400',
      punctuation: 'text-gray-600',
      collapseIcon: 'text-gray-500 hover:text-gray-300 cursor-pointer select-none',
      expandIcon: 'text-gray-500 hover:text-gray-300 cursor-pointer select-none',
    }

    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm">
            详细
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[700px] max-h-[600px] overflow-auto" align="start">
          <div className="space-y-3">
            <h4 className="font-semibold text-base border-b pb-2">{title}</h4>
            {typeof parsedData === 'object' ? (
              <div className="bg-muted p-4 rounded-lg border">
                <JsonView
                  data={parsedData}
                  shouldExpandNode={allExpanded}
                  style={customDarkJsonStyle}
                />
              </div>
            ) : (
              <pre className="text-sm bg-muted p-4 rounded-lg border whitespace-pre-wrap font-mono leading-relaxed">
                {parsedData}
              </pre>
            )}
          </div>
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <div className="h-full p-6 overflow-auto">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">API 日志</h1>
          <Button variant="outline" size="sm" onClick={getData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            刷新
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-3">
          <Input
            className="w-48"
            value={account}
            onChange={(event) => setAccount(event.target.value)}
            placeholder="模糊搜索账号..."
          />

          <SearchableSelect
            className="w-64"
            value={token}
            onChange={setToken}
            options={tokens
              .filter((t) => t.key && t.key.trim() !== '')
              .map((t) => ({
                value: t.key,
                label: `${t.key} (${t.doc_count})`,
              }))}
            placeholder="选择Token"
            searchPlaceholder="搜索Token..."
            emptyText="未找到Token"
            allowClear
          />

          <Input
            className="flex-1"
            placeholder="模糊搜索 URL 路径..."
            value={path}
            onChange={(e) => setPath(e.target.value)}
          />
          <Button
            variant="outline"
            onClick={handleClearFilters}
            disabled={!account && !token && !path}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            清空筛选
          </Button>
        </div>

        {/* Table */}
        <div className="border rounded-lg overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-32">Name</TableHead>
                <TableHead className="w-24">Version</TableHead>
                <TableHead className="w-48">URL</TableHead>
                <TableHead className="w-24">Header</TableHead>
                <TableHead className="w-24">Request</TableHead>
                <TableHead className="w-24">Resp Header</TableHead>
                <TableHead className="w-24">Response</TableHead>
                <TableHead className="w-40">DateTime</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.length > 0 ? (
                data.map((log, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-medium">{log.name}</TableCell>
                    <TableCell className="text-xs">
                      {log.request.headers?.version || '-'}
                    </TableCell>
                    <TableCell className="font-mono text-xs truncate max-w-xs">
                      {log.request.url.split('?')[0]}
                    </TableCell>
                    <TableCell>
                      <JsonPopover data={log.request.headers} title="Request Headers" />
                    </TableCell>
                    <TableCell>
                      {log.request.body ? (
                        <JsonPopover data={log.request.body} title="Request Body" />
                      ) : (
                        <span className="text-muted-foreground">null</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {log.response?.header ? (
                        <JsonPopover data={log.response.header} title="Response Headers" />
                      ) : (
                        <span className="text-muted-foreground">null</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {log.response?.body ? (
                        <JsonPopover data={log.response.body} title="Response Body" />
                      ) : (
                        <span className="text-muted-foreground">null</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatTime(log.timestamp)}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                    {loading ? '加载中...' : '暂无数据'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        <div className="text-sm text-muted-foreground">共 {data.length} 条记录</div>
      </div>
    </div>
  )
}
